import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Download, Loader2, CheckCircle } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const LeadCaptureModal = ({ isOpen, onClose, whitepaper }) => {
  const [formData, setFormData] = useState({ name: '', email: '' });
  const [status, setStatus] = useState('idle'); // idle, loading, success, error
  const [errorMessage, setErrorMessage] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('loading');

    try {
      const response = await axios.post(
        `${API}/whitepapers/${whitepaper?.id}/download`,
        {
          name: formData.name,
          email: formData.email,
          source: 'whitepaper',
          whitepaper_id: whitepaper?.id,
        }
      );
      setStatus('success');
      
      // Simulate download after success
      setTimeout(() => {
        onClose();
        setStatus('idle');
        setFormData({ name: '', email: '' });
      }, 2000);
    } catch (error) {
      setStatus('error');
      setErrorMessage('Something went wrong. Please try again.');
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md bg-white border border-slate-200 shadow-xl" data-testid="lead-capture-modal">
        <DialogHeader>
          <DialogTitle className="font-['Montserrat'] text-[#2C3E50]">
            {status === 'success' ? 'Download Ready!' : 'Download Whitepaper'}
          </DialogTitle>
          <DialogDescription className="text-[#64748B]">
            {status === 'success'
              ? 'Your download will start shortly.'
              : 'Enter your details to access the whitepaper.'}
          </DialogDescription>
        </DialogHeader>

        {status === 'success' ? (
          <div className="py-6 text-center" data-testid="lead-capture-success">
            <CheckCircle className="mx-auto text-[#27AE60] mb-4" size={48} />
            <p className="text-[#2C3E50] font-medium">{whitepaper?.title}</p>
            <p className="text-[#64748B] text-sm mt-2">Preparing your download...</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            {whitepaper && (
              <div className="bg-[#F8F9FA] rounded-lg p-4 mb-4">
                <p className="text-sm font-medium text-[#2C3E50]">{whitepaper.title}</p>
                <p className="text-xs text-[#64748B] mt-1">{whitepaper.description?.slice(0, 100)}...</p>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="name" className="text-[#2C3E50]">
                Name *
              </Label>
              <Input
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Your name"
                required
                className="border-slate-200 focus:border-[#3498DB]"
                data-testid="lead-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-[#2C3E50]">
                Work Email *
              </Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="your@company.com"
                required
                className="border-slate-200 focus:border-[#3498DB]"
                data-testid="lead-email-input"
              />
            </div>

            {status === 'error' && (
              <p className="text-red-500 text-sm" data-testid="lead-capture-error">{errorMessage}</p>
            )}

            <Button
              type="submit"
              disabled={status === 'loading'}
              className="w-full bg-[#3498DB] hover:bg-[#2980B9] text-white shadow-[0_4px_14px_0_rgba(52,152,219,0.39)]"
              data-testid="lead-capture-submit"
            >
              {status === 'loading' ? (
                <>
                  <Loader2 className="animate-spin mr-2" size={18} />
                  Processing...
                </>
              ) : (
                <>
                  <Download size={18} className="mr-2" />
                  Download Whitepaper
                </>
              )}
            </Button>

            <p className="text-xs text-center text-[#64748B]">
              By downloading, you agree to receive occasional updates. Unsubscribe anytime.
            </p>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
